import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_STARTER, CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE, SALES_HUB_PROFESSIONAL, SALES_HUB_STARTER } from 'self-service-api/constants/ApiNames';
import { PRODUCT_HUBSPOT_FREE } from 'self-service-api/constants/ProductIds';
import { currentOrderedPaidProductLevels, ENTERPRISE, FREE, orderedProductLevels, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { SOFTWARE } from 'self-service-api/constants/ProductTypes';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { CORE_SEAT } from 'self-service-api/constants/UnitNames';
import { CONTACT, MARKETING_CONTACT_DEPRECATED, USER } from 'self-service-api/constants/UnitTypes';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import getProductBillingNameKey from 'self-service-api/core/utilities/getProductBillingNameKey';
import { isMarketingContactsSku } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { isCoreSeatSku } from 'self-service-api/core/utilities/seatUtils';
import { getIsUngatedForSeats } from './gatingUtils';
import { getHasSkuInProductInventory } from './productInventoryUtils';
import { hasAnySeatsSkuInPromotions, hasAnyTBSkuInPromotions } from './promotionUtils';
export const CoreSeatApiNameByPaidProductLevel = {
  [STARTER]: CORE_SEATS_STARTER,
  [PROFESSIONAL]: CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: CORE_SEATS_ENTERPRISE
};
export const getProduct = ({
  productData: {
    productMap,
    productLineLevelToProductId,
    apiNameToProductId
  }
}, {
  productId,
  apiName,
  productLine,
  productLevel
}) => {
  let productIdToUse;
  if (productId) productIdToUse = productId;else if (apiName) productIdToUse = apiNameToProductId[apiName];else if (productLine && productLevel) {
    if (productLevel === FREE) {
      productIdToUse = PRODUCT_HUBSPOT_FREE;
    } else {
      productIdToUse = productLineLevelToProductId && productLineLevelToProductId[productLine] && productLineLevelToProductId[productLine][productLevel];
    }
  }
  if (!productIdToUse) return null;
  return productMap[productIdToUse];
};
export const getSkuMap = ({
  productData: {
    skuMap
  }
}) => skuMap;
export const getSku = (state, {
  skuId
}) => {
  return getSkuMap(state)[skuId];
};
export const getProductForSkuId = ({
  productData: {
    skuMap,
    productMap
  }
}, {
  skuId
}) => {
  const {
    productId
  } = skuMap[skuId];
  return productMap[productId];
};
export const getProductVersion = (state, {
  skuId,
  apiName,
  versionNumber
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;

  if (skuId) {
    const sku = skuMap[skuId];
    return sku.skuType === QUANTITY_PACK ? skuMap[sku.productVersionSkuId] : sku;
  }

  const allProductVersions = Object.values(skuMap).filter(sku => sku.skuType === PRODUCT_VERSION && getProductForSkuId(state, {
    skuId: sku.skuId
  }).apiName === apiName);
  return allProductVersions.find(({
    versionNumber: searchVersionNumber
  }) => searchVersionNumber === versionNumber);
};
export const getCurrentProductVersion = (state, productIdentifier) => {
  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const {
    currentSkuId
  } = product;
  return getProductVersion(state, {
    skuId: currentSkuId
  });
};
export const getCoreSeatQuantityBySkuId = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const currentProductVersion = skuMap[skuId];

  if (currentProductVersion) {
    const {
      includedUnits
    } = currentProductVersion;
    const seatUnit = includedUnits && includedUnits.find(value => {
      return value.name === CORE_SEAT;
    });

    if (seatUnit && seatUnit.quantity) {
      return seatUnit.quantity;
    }
  }

  return 0;
};
export const getSkuIdForFreeProduct = state => {
  const product = getCurrentProductVersion(state, {
    productId: PRODUCT_HUBSPOT_FREE
  });
  return product && product.skuId || 0;
};
export const getCurrentSkuIdForProduct = (state, productIdentifier) => {
  const {
    productLevel
  } = productIdentifier;

  if (productLevel === FREE) {
    return getSkuIdForFreeProduct(state);
  }

  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const currentProductVersion = getCurrentProductVersion(state, {
    productId: product.id
  });
  return currentProductVersion && currentProductVersion.skuId;
};
const CRM_SUITE_PRODUCTS = {
  [STARTER]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [PROFESSIONAL]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [ENTERPRISE]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS]
};
const CRM_SUITE_PRODUCT_LEVELS = [STARTER, PROFESSIONAL, ENTERPRISE];
const TB_SUITE_LEVEL_TO_API_NAME = {
  [STARTER]: CRM_SUITE_STARTER_BUNDLE,
  [PROFESSIONAL]: CRM_SUITE_PROFESSIONAL_BUNDLE,
  [ENTERPRISE]: CRM_SUITE_ENTERPRISE_BUNDLE
};
export const CORE_SEAT_LEVEL_TO_API_NAME = {
  [STARTER]: CORE_SEATS_STARTER,
  [PROFESSIONAL]: CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: CORE_SEATS_ENTERPRISE
};
export const getCurrentSkuIdsForProductLine = (state, {
  productLine
}) => {
  const {
    productData: {
      productLineLevelToProductId
    }
  } = state;
  return currentOrderedPaidProductLevels.map(productLevel => {
    const productId = productLineLevelToProductId[productLine][productLevel];
    return getCurrentSkuIdForProduct(state, {
      productId
    });
  });
};
export const getCrmSuiteProductLinesForProductLevel = (state, {
  productLevel
}) => {
  return CRM_SUITE_PRODUCTS[productLevel] || [];
};
export const getTBSuiteProductForProductLevel = (state, {
  suiteProductLevel
}) => {
  const apiName = TB_SUITE_LEVEL_TO_API_NAME[suiteProductLevel];
  return getProduct(state, {
    apiName
  });
};
export const getCurrentSuiteSkuIdsForProductLevel = (state, {
  productLevel
}) => {
  const tbProduct = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel: productLevel
  });

  if (tbProduct) {
    const tbSkuId = getCurrentSkuIdForProduct(state, {
      productId: tbProduct.id
    });
    return tbSkuId ? [tbSkuId] : [];
  }

  const crmSuiteProductLines = getCrmSuiteProductLinesForProductLevel(state, {
    productLevel
  });
  return crmSuiteProductLines.map(productLine => {
    return getCurrentSkuIdForProduct(state, {
      productLine,
      productLevel
    });
  }).filter(skuId => !!skuId);
};

const getBundledProductListForSkuId = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });

  if (sku && sku.skuType === PRODUCT_VERSION) {
    return sku.bundledProducts || [];
  }

  return [];
};

export const getBundledProductsForSkuId = (state, {
  skuId
}) => {
  const bundledList = getBundledProductListForSkuId(state, {
    skuId
  });
  return bundledList.map(item => {
    return getProduct(state, {
      productId: item.productId
    });
  });
};
export const getBundledProductVersionsForSkuId = (state, {
  skuId
}) => {
  const bundledList = getBundledProductListForSkuId(state, {
    skuId
  });
  return bundledList.map(item => {
    return getCurrentProductVersion(state, {
      productId: item.productId
    });
  });
};
export const skuIdHasBundledProducts = (state, {
  skuId
}) => {
  const bundledProducts = getBundledProductsForSkuId(state, {
    skuId
  });
  return bundledProducts.length > 0;
};
export const productHasBundledProducts = (state, {
  productId
}) => {
  const skuId = getCurrentSkuIdForProduct(state, {
    productId
  });
  return skuId ? skuIdHasBundledProducts(state, {
    skuId
  }) : false;
};
export const getCrmSuiteProductLevels = () => {
  return CRM_SUITE_PRODUCT_LEVELS;
};
export const isCrmSuiteProductLevel = productLevel => {
  const productLevels = getCrmSuiteProductLevels(); // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore

  return productLevels.includes(productLevel);
};
export const getProductLevelForSkuId = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return product.productLevel;
};
export const getProductSkuIdIsBundle = (state, {
  skuId
}) => {
  const productVersion = getProductVersion(state, {
    skuId
  });
  return productVersion.isBundle;
};
export const getBundledProductLinesForSkuId = (state, {
  skuId
}) => {
  const isBundle = getProductSkuIdIsBundle(state, {
    skuId
  });

  if (!isBundle) {
    return [];
  }

  const productVersion = getProductVersion(state, {
    skuId
  });
  return productVersion.bundledProducts.map(subProductVersion => {
    const {
      productId
    } = subProductVersion;
    const subProduct = getProduct(state, {
      productId
    });
    return subProduct.productLine;
  });
};
export const getTBSuiteProductLevelForSkuIds = (state, {
  skuIds
}) => {
  // Create an object keyed by productLevel -> productLine -> boolean
  const foundProducts = Object.keys(TB_SUITE_LEVEL_TO_API_NAME).reduce((acc, productLevel) => {
    const {
      skuId: tbSuiteSkuId
    } = getCurrentProductVersion(state, {
      apiName: TB_SUITE_LEVEL_TO_API_NAME[productLevel]
    });
    const productLines = getBundledProductLinesForSkuId(state, {
      skuId: tbSuiteSkuId
    });
    return Object.assign({}, acc, {
      [productLevel]: productLines.reduce((productLevelAcc, productLine) => Object.assign({}, productLevelAcc, {
        [productLine]: false
      }), {})
    });
  }, {});
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  skuIds.forEach(skuId => {
    const {
      productId,
      skuType
    } = skuMap[skuId];
    const {
      productLine,
      productLevel
    } = productMap[productId];

    if (skuType === PRODUCT_VERSION && productLine && productLevel && isCrmSuiteProductLevel(productLevel) && productLine in foundProducts[productLevel]) {
      foundProducts[productLevel][productLine] = true;
    }
  });

  for (const productLevel of Object.keys(foundProducts)) {
    const productLines = Object.keys(foundProducts[productLevel]);

    if (productLines.every(productLine => foundProducts[productLevel][productLine])) {
      return productLevel;
    }
  }

  return null;
}; // For 5 hub unbundled suite only

export const getCrmSuiteProductLevelForSkuIds = (state, {
  skuIds
}) => {
  // Create an object keyed by productLevel -> productLine -> boolean
  const suiteProductLevels = getCrmSuiteProductLevels();
  const foundProducts = suiteProductLevels.reduce((acc, productLevel) => {
    const productLines = getCrmSuiteProductLinesForProductLevel(state, {
      productLevel
    });
    return Object.assign({}, acc, {
      [productLevel]: productLines.reduce((productLevelAcc, productLine) => Object.assign({}, productLevelAcc, {
        [productLine]: false
      }), {})
    });
  }, {});
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  skuIds.forEach(skuId => {
    const {
      productId,
      skuType
    } = skuMap[skuId];
    const {
      productLine,
      productLevel
    } = productMap[productId];

    if (skuType === PRODUCT_VERSION && productLine && productLevel && isCrmSuiteProductLevel(productLevel) && productLine in foundProducts[productLevel]) {
      foundProducts[productLevel][productLine] = true;
    }
  });

  for (const productLevel of Object.keys(foundProducts)) {
    const productLines = Object.keys(foundProducts[productLevel]);

    if (productLines.every(productLine => foundProducts[productLevel][productLine])) {
      return productLevel;
    }
  }

  return null;
};
export const getSkuIdIsInTBSuiteForProductLevel = (state, {
  skuId,
  suiteProductLevel
}) => {
  if (!suiteProductLevel) {
    return false;
  }

  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const suiteProduct = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel
  });
  const currentSuiteProductVersion = getCurrentProductVersion(state, {
    productId: suiteProduct.id
  });
  const suiteProductLines = getBundledProductLinesForSkuId(state, {
    skuId: currentSuiteProductVersion.skuId
  });
  return suiteProductLines.includes(productLine) && productLevel === suiteProductLevel;
}; // For 5 hub unbundled suite only

export const getSkuIdIsInCrmSuite = (state, {
  skuId,
  suiteProductLevel
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const {
    productLevel,
    productLine,
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const crmSuiteProductLines = suiteProductLevel ? getCrmSuiteProductLinesForProductLevel(state, {
    productLevel: suiteProductLevel
  }) : []; // Core Seats are similar to quantity packs in that they can be purchased
  // separately from suite, but they can also be considered part of suite

  const isCoreSeat = isUngatedForSeats && isCoreSeatSku(apiName);
  return crmSuiteProductLines.includes(productLine) && productLevel === suiteProductLevel || isCoreSeat;
};
export const getSkuIdsContainOnlyTBSuite = (state, {
  skuIds
}) => {
  const suiteProductLevel = getTBSuiteProductLevelForSkuIds(state, {
    skuIds
  });
  if (!suiteProductLevel) return false;
  return skuIds.every(skuId => getSkuIdIsInTBSuiteForProductLevel(state, {
    skuId,
    suiteProductLevel
  }));
}; // For 5 hub unbundled suite only

export const getSkuIdsContainOnlyCRMSuite = (state, {
  skuIds
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIds(state, {
    skuIds
  });
  if (!suiteProductLevel) return false;
  return skuIds.every(skuId => getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  }));
};
export const getTBSuiteProductLevelForProductConfigurations = (state, {
  productConfigurations
}) => getTBSuiteProductLevelForSkuIds(state, {
  skuIds: productConfigurations.map(({
    skuId
  }) => skuId)
}); // For 5 hub unbundled suite only

export const getCrmSuiteProductLevelForProductConfigurations = (state, {
  productConfigurations
}) => getCrmSuiteProductLevelForSkuIds(state, {
  skuIds: productConfigurations.map(({
    skuId
  }) => skuId)
});
export const getHubProductVersionSkuIds = (state, {
  skuIds
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return skuIds.filter(skuId => {
    const sku = skuMap[skuId];
    const product = getProductForSkuId(state, {
      skuId
    });
    return sku.skuType === PRODUCT_VERSION && product.productType === SOFTWARE && product.productLine && product.productLevel;
  });
};
export const getHubProductVersionSkuIdsOrDefault = (state, {
  skuIds
}) => {
  const filtered = getHubProductVersionSkuIds(state, {
    skuIds
  });
  return filtered.length ? filtered : skuIds;
};
export const getBillingNameKey = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const product = getProductForSkuId(state, {
    skuId
  });
  const sku = skuMap[skuId];

  if (sku.skuType === QUANTITY_PACK) {
    return getProductBillingNameKey({
      sku
    });
  } else if (sku.skuType === PRODUCT_VERSION) {
    if (!product) return null;
    const {
      apiName
    } = product;
    return getProductBillingNameKey({
      apiName,
      sku
    });
  }

  return null;
};
export const getQuantityPackDetailsListForProductVersion = (state, {
  productVersionSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;

  if (!productVersionSkuId) {
    return [];
  }

  const {
    quantityPackDetails
  } = skuMap[productVersionSkuId];
  return quantityPackDetails || [];
};
export const getQuantityPackForProductVersion = (state, {
  productVersionSkuId,
  unitType,
  unitScope
}) => {
  const quantityPackDetails = getQuantityPackDetailsListForProductVersion(state, {
    productVersionSkuId
  });
  const MARKETING_UNIT_TYPES = [CONTACT, MARKETING_CONTACT_DEPRECATED];
  const unitTypes = MARKETING_UNIT_TYPES.includes(unitType) ? MARKETING_UNIT_TYPES : [unitType];
  return quantityPackDetails.find(quantityPack => {
    const quantityUnit = quantityPack && quantityPack.quantityUnit;
    return unitTypes.includes(quantityUnit.unitType) && unitScope === quantityUnit.unitScope;
  });
};
export const getProductVersionHasQuantityPack = (state, {
  productVersionSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    quantityPackDetails
  } = skuMap[productVersionSkuId];
  return !!quantityPackDetails.length;
};
export const getIncludedCoreSeatsQuantityForProductVersion = (state, {
  productVersionSkuId
}) => {
  const {
    productLevel
  } = getProductForSkuId(state, {
    skuId: productVersionSkuId
  });
  return currentOrderedPaidProductLevels.includes(productLevel) ? getCoreSeatQuantityBySkuId(state, {
    skuId: productVersionSkuId
  }) : 0;
};
export const getIncludedCoreSeatsQuantityForSuiteProductLevel = (state, {
  suiteProductLevel
}) => {
  const product = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel
  });

  if (!product) {
    if (suiteProductLevel === STARTER) return 1;
    const suiteSkuIds = getCurrentSuiteSkuIdsForProductLevel(state, {
      productLevel: suiteProductLevel
    });
    return suiteSkuIds.reduce((currentSum, currentValue) => {
      return currentSum + getCoreSeatQuantityBySkuId(state, {
        skuId: currentValue
      });
    }, 0);
  }

  const {
    currentSkuId
  } = product;
  return getCoreSeatQuantityBySkuId(state, {
    skuId: currentSkuId
  });
};
export const getSkuIdIsHub = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  return !!productLine && !!productLevel && skuType === PRODUCT_VERSION;
};
export const getSkuIdIsQuantityPack = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    skuType
  } = skuMap[skuId];
  return skuType === QUANTITY_PACK;
};
export const highestProductLevel = (...productLevels) => orderedProductLevels[Math.max(0, ...productLevels.map(productLevel => orderedProductLevels.indexOf(productLevel)))];
export const getHighestProductLevelFromSkuIds = (state, {
  skuIds
}) => {
  const productLevels = skuIds.map(skuId => {
    const {
      productLevel
    } = getProductForSkuId(state, {
      skuId
    });
    return productLevel;
  }).filter(productLevel => !!productLevel);
  return highestProductLevel(...productLevels);
};
export const getHighestProductLevelSkuIdsByProductLine = (state, {
  skuIds
}) => {
  return skuIds.reduce((agr, skuId) => {
    const {
      productLine: singleSkuProductLine,
      productLevel,
      currentSkuId: productSkuId
    } = getProductForSkuId(state, {
      skuId
    });
    const skuIdToUse = getSkuIdIsQuantityPack(state, {
      skuId
    }) ? productSkuId : skuId;
    const bundledProductLines = getBundledProductLinesForSkuId(state, {
      skuId
    });
    const allPossibleProductLines = [singleSkuProductLine, ...bundledProductLines];
    allPossibleProductLines.forEach(productLine => {
      const currentSkuIdForProductLine = agr[productLine];

      if (currentSkuIdForProductLine) {
        const {
          productLevel: productLevelForCurrentSkuId
        } = getProductForSkuId(state, {
          skuId: currentSkuIdForProductLine
        });
        const isHigherProductLevel = orderedProductLevels.indexOf(productLevel) > orderedProductLevels.indexOf(productLevelForCurrentSkuId);
        agr[productLine] = isHigherProductLevel ? skuIdToUse : currentSkuIdForProductLine;
      } else {
        agr[productLine] = skuIdToUse;
      }
    });
    return agr;
  }, {});
};
export const getSkuIdIsAddonOrService = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel,
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  const coreSeatSku = isCoreSeatSku(apiName);
  if (coreSeatSku) return false;
  return (!productLine || !productLevel) && skuType === PRODUCT_VERSION;
};
export const getMaxProductVersionQuantity = (state, {
  skuId
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    versionNumber
  } = skuMap[skuId];
  const productVersion = getProductVersion(state, {
    apiName,
    versionNumber
  });
  const entry = productVersion.maxTouchlessPurchasableQuantity;
  return entry === null ? Infinity : entry || 1;
};
export const getLowestPaidProductLevel = (state, {
  productLine
}) => currentOrderedPaidProductLevels.find(productLevel => {
  const product = getProduct(state, {
    productLine,
    productLevel
  });
  return !!product;
});
export const getIsMarketingContactsSku = (state, {
  skuId
}) => {
  const {
    skuId: productVersionSkuId
  } = getProductVersion(state, {
    skuId
  });
  return isMarketingContactsSku({
    skuMap: getSkuMap(state),
    skuId: productVersionSkuId
  });
};
export const getLowestPaidProductLevelFromList = (state, {
  productLine,
  productLevels
}) => currentOrderedPaidProductLevels.filter(productLevel => productLevels.includes(productLevel)).find(productLevel => {
  const product = getProduct(state, {
    productLine,
    productLevel
  });
  return !!product;
});
export const getPaidProductsLevelsForProductLine = (state, {
  productLine
}) => currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
  productLine,
  productLevel
}));
export const getPaidProductsForProductLine = (state, {
  productLine
}) => {
  const filteredLevels = currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
    productLine,
    productLevel
  }));
  if (!filteredLevels.length) return [];
  const products = filteredLevels.map(productLevel => getProduct(state, {
    productLine,
    productLevel
  }));
  return products;
};
export const getIsSalesOrServiceProOrEntSku = (state, {
  skuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  return [SALES, SERVICE].includes(productLine) && [PROFESSIONAL, ENTERPRISE].includes(productLevel);
};
export const getQuantifiableSkusForProductLine = (state, {
  productLine
}) => {
  const paidProducts = getPaidProductsForProductLine(state, {
    productLine
  });
  const skus = paidProducts.map(product => {
    const currentVersion = getCurrentProductVersion(state, {
      apiName: product.apiName
    });
    return currentVersion;
  });
  const quantifiableSkus = skus.filter(sku => sku && sku.quantifiable);
  return quantifiableSkus;
};
export const getIsCrmSuiteSalesOrServiceSkuProEnt = (state, {
  skuId,
  suiteProductLevel
}) => {
  const isProductLevelProOrEnt = [PROFESSIONAL, ENTERPRISE].includes(suiteProductLevel);
  const {
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const isSkuHub = getSkuIdIsHub(state, {
    skuId
  });
  const isSkuInCrmSuite = getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  });
  const isProductLineSalesOrService = [SALES, SERVICE].includes(productLine);
  return isSkuInCrmSuite && isSkuHub && isProductLineSalesOrService && isProductLevelProOrEnt;
};
export const getProductDataIsEmpty = state => {
  const {
    productData
  } = state;
  return Object.values(productData).every(val => {
    if (!val || Object.keys(val).length === 0) return true;
    return false;
  });
};
export const getIsCoreSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return isCoreSeatSku(product.apiName);
};
export const getIsPaidCoreSeatSku = (state, {
  skuId
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  return getIsCoreSeatSku(state, {
    skuId
  }) && [CORE_SEATS_STARTER, CORE_SEATS_PROFESSIONAL, CORE_SEATS_ENTERPRISE].includes(apiName);
};
export const getCoreSeatSkuForProductLevel = (state, {
  productLevel
}) => {
  const apiName = CORE_SEAT_LEVEL_TO_API_NAME[productLevel];
  const productVersion = getCurrentProductVersion(state, {
    apiName
  });
  return productVersion;
}; // This is a clone of `isQuantityUpgrade` from self-service-api but without the
// extra API calls and Promises

export const isQuantityUpgrade = (state, {
  productConfigurations
}) => {
  if (!productConfigurations || !productConfigurations.length) {
    return false;
  }

  const isQuantityUpgradeMap = productConfigurations.map(({
    skuId
  }) => {
    // Add special case for sales starter and sales pro legacy additional seat purchase
    const {
      apiName
    } = getProductForSkuId(state, {
      skuId
    });
    const {
      versionNumber
    } = getProductVersion(state, {
      skuId
    });

    if (apiName === SALES_HUB_STARTER && versionNumber === VersionNumbers[SALES_HUB_STARTER].LEGACY) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }

    if (apiName === SALES_HUB_PROFESSIONAL && versionNumber === VersionNumbers[SALES_HUB_PROFESSIONAL].LEGACY_BUSINESS_USER) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    } // Update for Seats -> treat additional core seat purchases as if they're quantity packs


    if (isCoreSeatSku(apiName)) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    } // Update for Seats -> treat sales/service at pro/ent as if they're quantity packs


    if (getIsSalesOrServiceProOrEntSku(state, {
      skuId
    })) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }

    return getSkuIdIsQuantityPack(state, {
      skuId
    });
  });
  return isQuantityUpgradeMap.every(upgrade => !!upgrade);
};
export const isQuantifiableHub = (state, {
  skuId
}) => {
  const isHub = getSkuIdIsHub(state, {
    skuId
  });
  const productVersion = getProductVersion(state, {
    skuId
  });
  return isHub && productVersion.quantifiable;
};
export const isMarketingContactsQuantityPack = (state, {
  skuId
}) => {
  const isQuantityPack = getSkuIdIsQuantityPack(state, {
    skuId
  });

  if (isQuantityPack) {
    const sku = getSku(state, {
      skuId
    });
    const unitType = sku && sku.quantityUnit && sku.quantityUnit.unitType;
    return [CONTACT, MARKETING_CONTACT_DEPRECATED].includes(unitType);
  }

  return false;
}; // eventually will want to add Sales/Service seats that are quantifiable hubs

export const isPaidSalesServiceSeat = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  const product = getProductForSkuId(state, {
    skuId
  });
  const isQuantityPack = getSkuIdIsQuantityPack(state, {
    skuId
  });
  const isSalesOrServiceQuantityPack = isQuantityPack && sku.quantityUnit && sku.quantityUnit.unitType === USER;
  const parentSkuIsTBSku = Object.values(TB_SUITE_LEVEL_TO_API_NAME).includes(product && product.apiName);
  return parentSkuIsTBSku && isSalesOrServiceQuantityPack;
};
export const getIncludedUnitsForQuantityPack = (state, {
  skuId
}) => {
  const isQuantityPack = getSkuIdIsQuantityPack(state, {
    skuId
  });

  if (!isQuantityPack) {
    return 0;
  }

  const quantityPack = getSku(state, {
    skuId
  });
  return quantityPack && quantityPack.quantityUnit && quantityPack.quantityUnit.includedWithProductVersion || 0;
};
export const hasAnyTBSkuInProductData = state => {
  if (getProductDataIsEmpty(state)) return null;
  const tbApiNames = CRM_SUITE_PRODUCT_LEVELS.map(productLevel => TB_SUITE_LEVEL_TO_API_NAME[productLevel]);
  return tbApiNames.some(apiName => getProduct(state, {
    apiName
  }));
};
export const hasAnySeatsSkuInProductData = state => {
  if (getProductDataIsEmpty(state)) return null;
  const coreSeatApiNames = currentOrderedPaidProductLevels.map(productLevel => CORE_SEAT_LEVEL_TO_API_NAME[productLevel]);
  return coreSeatApiNames.some(apiName => getProduct(state, {
    apiName
  }));
};
const cohorts = {
  NON_SEATS: 'NON_SEATS',
  SEATS_ONLY: 'SEATS_ONLY',
  TRUE_BUNDLING: 'TRUE_BUNDLING'
};
export const hasMisMatchedProductsAndPromotions = state => {
  const hasAnyTBSkuInPromotionsResult = hasAnyTBSkuInPromotions(state);
  const hasAnySeatsSkuInPromotionsResult = hasAnySeatsSkuInPromotions(state);
  const hasAnyTBSkuInProductDataResult = hasAnyTBSkuInProductData(state);
  const hasAnySeatsSkuInProductDataResult = hasAnySeatsSkuInProductData(state);
  const promotionsCohort = hasAnyTBSkuInPromotionsResult ? cohorts.TRUE_BUNDLING : hasAnySeatsSkuInPromotionsResult ? cohorts.SEATS_ONLY : cohorts.NON_SEATS;
  const productsCohort = hasAnyTBSkuInProductDataResult ? cohorts.TRUE_BUNDLING : hasAnySeatsSkuInProductDataResult ? cohorts.SEATS_ONLY : cohorts.NON_SEATS;
  return promotionsCohort !== productsCohort;
};